<template>
  <div class="stepper">
    <img class="stepper__logo" src="@/assets.app/images/logo.svg" />

    <p class="stepper__section-name">
      Step {{ view.currentSectionIndex }} <strong>•</strong>
      {{ view.currentSection.title }}
    </p>

    <Transition name="slide" mode="out-in">
      <div :key="view.currentSectionIndex" class="stepper__steps">
        <div
          v-for="(section, index) in view.sections.slice(1)"
          :key="section.id"
          class="stepper__step"
          :class="{
            'stepper__step--prev': index + 1 < view.currentSectionIndex,
            'stepper__step--current': index + 1 === view.currentSectionIndex,
            'stepper__step--next': index + 1 > view.currentSectionIndex,
          }"
          :style="{
            '--section-progress': view.currentSectionProgress,
          }"
        ></div>
      </div>
    </Transition>
  </div>
</template>

<script setup lang="ts">
import { QuestionnaireView } from '@/models/onboarding'

defineProps<{
  view: QuestionnaireView
}>()
</script>

<style lang="scss" scoped>
$step-min-width: 16px;

.stepper {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  padding-block: 40px 16px;
  padding-inline: var(--onboarding-padding);
  background: $c-white;
  border-bottom: 1px solid $c-border-7;
  @supports (padding-top: env(safe-area-inset-top)) {
    padding-top: max(40px, env(safe-area-inset-top));
  }
  @media (max-height: 650px) {
    padding-block: 24px 8px;
    @supports (padding-top: env(safe-area-inset-top)) {
      padding-top: max(24px, env(safe-area-inset-top));
    }
  }
  @media (min-width: $w-pc-b) {
    background: transparent;
    border: none;
    padding-bottom: 0;
  }

  &__logo {
    display: none;
    @media (min-width: $w-pc-b) and (min-height: 900px) {
      display: block;
      margin-bottom: 32px;
    }
  }

  &__section-name {
    font-size: 14px;
    line-height: 1;
    color: $c-black-6;
    margin-bottom: 10px;
  }

  &__steps {
    display: flex;
    gap: 8px;
    width: 100%;
    max-width: var(--onboarding-inner-width);
    @media (min-width: $w-pc-b) {
      max-width: 600px;
    }
  }

  &__step {
    height: 6px;
    min-width: $step-min-width;
    border-radius: 6px;
    background: $c-border-7;
  }

  &__step--prev {
    position: relative;
    background: $c-yellow;
  }

  &__step--current {
    position: relative;
    width: 100%;

    &:after {
      content: '';
      display: block;
      width: calc(100% * var(--section-progress));
      height: 100%;
      border-radius: 3px;
      background-color: $c-yellow;
      transition: width 0.3s ease;
    }
  }

  .onboarding-questionnaire:has(
      .onboarding-pact__loading-screen--final,
      .onboarding-pact__card--back.onboarding-pact__card--visible
    )
    &__step--current:after {
    width: 100%;
  }
}

.slide {
  &-leave-active {
    transition: all 1s ease;
  }

  &-leave-active .stepper__step--current {
    animation: 1s slide-out ease forwards;
  }

  &-leave-active .stepper__step--current + .stepper__step--next {
    animation: 1s slide-in ease forwards;
  }

  &-leave-active .stepper__step--current:after {
    transition: all 0.3s;
  }

  &-leave-to .stepper__step--current:after {
    width: 100%;
  }
}

@keyframes slide-out {
  0%,
  50% {
    width: 100%;
  }

  100% {
    width: $step-min-width;
  }
}

@keyframes slide-in {
  0%,
  50% {
    width: $step-min-width;
  }

  100% {
    width: 100%;
  }
}
</style>
